/**
 * ToolCard - Individual tool display with expand/collapse for parameters
 * 
 * Blue Steel Compliant:
 * - Zero hardcoded colors (CSS variables only)
 * - Smooth animations (0.2s cubic-bezier)
 * - WCAG 2.1 AA accessible (keyboard, ARIA)
 * - Hover lift effect (4px)
 * 
 * @example
 * ```tsx
 * <ToolCard
 *   tool={tool}
 *   selected={isSelected}
 *   onSelect={() => toggleTool(tool.id)}
 *   compact={false}
 * />
 * ```
 */

import React, { useState, useCallback } from 'react';
import type { MCPTool, ToolParameter, ToolMetadata } from './types';

// Re-export types for convenience
export type { MCPTool, ToolParameter, ToolMetadata };

export interface ToolCardProps {
  tool: MCPTool;
  selected?: boolean;
  onSelect?: (tool: MCPTool) => void;
  compact?: boolean;
  showParameters?: boolean;
  disabled?: boolean;
}

export const ToolCard: React.FC<ToolCardProps> = ({
  tool,
  selected = false,
  onSelect,
  compact = false,
  showParameters = true,
  disabled = false,
}) => {
  const [expanded, setExpanded] = useState(false);
  const [hovered, setHovered] = useState(false);

  const handleClick = useCallback(() => {
    if (!disabled && onSelect) {
      onSelect(tool);
    }
  }, [disabled, onSelect, tool]);

  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      handleClick();
    }
  }, [handleClick]);

  const toggleExpanded = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    setExpanded(prev => !prev);
  }, []);

  const parameterCount = tool.parameters?.length || 0;
  const requiredCount = tool.parameters?.filter(p => p.required).length || 0;

  return (
    <div
      role="option"
      aria-selected={selected}
      aria-disabled={disabled}
      tabIndex={disabled ? -1 : 0}
      onClick={handleClick}
      onKeyDown={handleKeyDown}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        padding: compact ? 10 : 14,
        background: selected
          ? 'color-mix(in srgb, var(--main-accent) 15%, transparent)'
          : hovered
          ? 'var(--card-hover-bg)'
          : 'var(--card-bg)',
        border: `1px solid ${selected ? 'var(--main-accent)' : 'var(--border-color)'}`,
        borderRadius: 10,
        cursor: disabled ? 'not-allowed' : 'pointer',
        opacity: disabled ? 0.5 : 1,
        transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
        transform: hovered && !disabled ? 'translateY(-2px)' : 'translateY(0)',
        boxShadow: selected
          ? '0 0 0 2px color-mix(in srgb, var(--main-accent) 25%, transparent)'
          : hovered
          ? 'var(--shadow-md)'
          : 'var(--shadow-sm)',
      }}
    >
      {/* Header Row */}
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'flex-start',
        gap: 12,
      }}>
        {/* Tool Info */}
        <div style={{ flex: 1, minWidth: 0 }}>
          {/* Name */}
          <div style={{
            fontSize: compact ? 12 : 13,
            fontWeight: 600,
            color: 'var(--main-text)',
            marginBottom: 4,
            wordBreak: 'break-word',
          }}>
            {tool.name}
          </div>
          
          {/* Description */}
          {!compact && (
            <div style={{
              fontSize: 11,
              color: 'var(--main-text-secondary)',
              lineHeight: 1.4,
              display: '-webkit-box',
              WebkitLineClamp: 2,
              WebkitBoxOrient: 'vertical',
              overflow: 'hidden',
            }}>
              {tool.description || 'No description provided'}
            </div>
          )}
        </div>

        {/* Selection Indicator */}
        <div
          aria-hidden="true"
          style={{
            width: 22,
            height: 22,
            borderRadius: '50%',
            border: `2px solid ${selected ? 'var(--main-accent)' : 'var(--border-color)'}`,
            background: selected ? 'var(--main-accent)' : 'transparent',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
            transition: 'all 0.2s ease',
          }}
        >
          {selected && (
            <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
              <path
                d="M2 6L5 9L10 3"
                stroke="white"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          )}
        </div>
      </div>

      {/* Metadata Badges */}
      {!compact && (
        <div style={{
          display: 'flex',
          gap: 6,
          flexWrap: 'wrap',
          marginTop: 10,
        }}>
          {/* Provider Badge */}
          {tool.metadata?.provider && (
            <span style={{
              padding: '2px 8px',
              borderRadius: 12,
              fontSize: 10,
              fontWeight: 600,
              background: 'color-mix(in srgb, var(--info-color) 15%, transparent)',
              color: 'var(--info-color)',
            }}>
              🏢 {tool.metadata.provider}
            </span>
          )}

          {/* Category Badge */}
          {tool.categories?.[0] && (
            <span style={{
              padding: '2px 8px',
              borderRadius: 12,
              fontSize: 10,
              fontWeight: 600,
              background: 'color-mix(in srgb, var(--main-accent) 15%, transparent)',
              color: 'var(--main-accent)',
            }}>
              {tool.categories[0]}
            </span>
          )}

          {/* Backend Type Badge */}
          <span style={{
            padding: '2px 8px',
            borderRadius: 12,
            fontSize: 10,
            fontWeight: 600,
            background: 'color-mix(in srgb, var(--success-color) 15%, transparent)',
            color: 'var(--success-color)',
          }}>
            {tool.metadata?.backend_type === 'workflow' ? '🔄 Workflow' :
             tool.metadata?.backend_type === 'command' ? '⚡ Command' : '🔧 Function'}
          </span>

          {/* Virtual Server Badge */}
          {tool.virtual_server_name && (
            <span style={{
              padding: '2px 8px',
              borderRadius: 12,
              fontSize: 10,
              fontWeight: 600,
              background: 'color-mix(in srgb, var(--warning-color) 15%, transparent)',
              color: 'var(--warning-color)',
            }}>
              📦 {tool.virtual_server_name}
            </span>
          )}
        </div>
      )}

      {/* Parameters Section */}
      {showParameters && parameterCount > 0 && (
        <div style={{ marginTop: 10 }}>
          {/* Parameters Toggle */}
          <button
            onClick={toggleExpanded}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 6,
              padding: 0,
              margin: 0,
              background: 'none',
              border: 'none',
              color: 'var(--main-text-secondary)',
              fontSize: 11,
              cursor: 'pointer',
              transition: 'color 0.2s ease',
            }}
            onMouseOver={e => (e.currentTarget.style.color = 'var(--main-accent)')}
            onMouseOut={e => (e.currentTarget.style.color = 'var(--main-text-secondary)')}
          >
            <span>📝 {parameterCount} parameter{parameterCount !== 1 ? 's' : ''}</span>
            {requiredCount > 0 && (
              <span style={{ color: 'var(--warning-color)' }}>
                ({requiredCount} required)
              </span>
            )}
            <span style={{
              transform: expanded ? 'rotate(180deg)' : 'rotate(0deg)',
              transition: 'transform 0.2s ease',
            }}>
              ▼
            </span>
          </button>

          {/* Expanded Parameters List */}
          {expanded && (
            <div
              style={{
                marginTop: 8,
                padding: 10,
                background: 'color-mix(in srgb, var(--main-text) 5%, transparent)',
                borderRadius: 8,
                animation: 'slideDown 0.2s ease',
              }}
              onClick={e => e.stopPropagation()}
            >
              {tool.parameters?.map((param, idx) => (
                <div
                  key={param.name}
                  style={{
                    padding: '6px 0',
                    borderBottom: idx < (tool.parameters?.length || 0) - 1
                      ? '1px solid var(--border-color)'
                      : 'none',
                  }}
                >
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 6,
                    marginBottom: 2,
                  }}>
                    <code style={{
                      fontSize: 11,
                      color: 'var(--main-accent)',
                      fontFamily: 'var(--font-mono)',
                    }}>
                      {param.name}
                    </code>
                    <span style={{
                      fontSize: 9,
                      padding: '1px 5px',
                      borderRadius: 4,
                      background: 'color-mix(in srgb, var(--info-color) 15%, transparent)',
                      color: 'var(--info-color)',
                    }}>
                      {param.type}
                    </span>
                    {param.required && (
                      <span style={{
                        fontSize: 9,
                        padding: '1px 5px',
                        borderRadius: 4,
                        background: 'color-mix(in srgb, var(--warning-color) 15%, transparent)',
                        color: 'var(--warning-color)',
                      }}>
                        required
                      </span>
                    )}
                  </div>
                  {param.description && (
                    <div style={{
                      fontSize: 10,
                      color: 'var(--main-text-secondary)',
                      marginTop: 2,
                    }}>
                      {param.description}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Inline keyframes for animation */}
      <style>{`
        @keyframes slideDown {
          from {
            opacity: 0;
            transform: translateY(-8px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
      `}</style>
    </div>
  );
};

